import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Ruler, Weight, Zap, Gauge, Plane, Radio, 
  Clock, MapPin, Play, X, ChevronLeft, ChevronRight, 
  Loader2, ExternalLink, Mountain
} from 'lucide-react';
import { vehicleGalleryAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const getLocalizedField = (item, field, language) => {
  if (!item) return '';
  if (language === 'tr') {
    const trField = item[`${field}_tr`];
    if (trField && trField.trim()) return trField;
  }
  return item[field] || '';
};

const getLocalizedSpec = (specs, field, language) => {
  if (!specs) return '';
  if (language === 'tr') {
    const trField = specs[`${field}_tr`];
    if (trField && trField.trim()) return trField;
  }
  return specs[field] || '';
};

// Extract YouTube video ID from URL
const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  // Set dynamic page title
  useEffect(() => {
    if (vehicle) {
      const title = language === 'tr' 
        ? (vehicle.name_tr || vehicle.name) 
        : vehicle.name;
      document.title = `${title} | Kartal Air`;
    }
    return () => {
      document.title = 'Kartal Air | SUAS 2026';
    };
  }, [vehicle, language]);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const data = await vehicleGalleryAPI.getById(id);
        setVehicle(data);
      } catch (err) {
        console.error('Failed to fetch vehicle:', err);
        setError('Vehicle not found');
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  // Close modals on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setLightboxImage(null);
        setShowVideo(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Get all images for lightbox navigation
  const getAllImages = () => {
    if (!vehicle) return [];
    const images = [vehicle.image];
    if (vehicle.additionalImages && vehicle.additionalImages.length > 0) {
      images.push(...vehicle.additionalImages);
    }
    return images.filter(Boolean);
  };

  const navigateLightbox = (direction) => {
    const images = getAllImages();
    if (direction === 'next') {
      setLightboxIndex((prev) => (prev + 1) % images.length);
    } else {
      setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
    }
    setLightboxImage(images[(lightboxIndex + (direction === 'next' ? 1 : -1) + images.length) % images.length]);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center" style={{ background: '#0F172A' }}>
        <Plane size={64} style={{ color: '#64748B' }} className="mb-4" />
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
          {language === 'tr' ? 'Araç Bulunamadı' : 'Vehicle Not Found'}
        </h2>
        <Link 
          to="/aircraft" 
          className="flex items-center gap-2 px-6 py-3 rounded-lg"
          style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}
        >
          <ArrowLeft size={20} />
          {language === 'tr' ? 'Araç Galerisine Dön' : 'Back to Vehicle Gallery'}
        </Link>
      </div>
    );
  }

  const specs = vehicle.specs || {};
  const youtubeId = extractYouTubeId(vehicle.videoUrl);
  const allImages = getAllImages();

  // Specs configuration for the table
  const specsList = [
    { key: 'wingspan', icon: Ruler, label: language === 'tr' ? 'Kanat Açıklığı' : 'Wingspan' },
    { key: 'mtow', icon: Weight, label: language === 'tr' ? 'Maks. Kalkış Ağırlığı (MTOW)' : 'Max Takeoff Weight (MTOW)' },
    { key: 'weight', icon: Weight, label: language === 'tr' ? 'Boş Ağırlık' : 'Empty Weight' },
    { key: 'cruiseSpeed', icon: Gauge, label: language === 'tr' ? 'Seyir Hızı' : 'Cruise Speed' },
    { key: 'maxSpeed', icon: Zap, label: language === 'tr' ? 'Maksimum Hız' : 'Max Speed' },
    { key: 'range', icon: MapPin, label: language === 'tr' ? 'Menzil' : 'Range' },
    { key: 'endurance', icon: Clock, label: language === 'tr' ? 'Uçuş Süresi' : 'Endurance' },
    { key: 'payload', icon: Plane, label: language === 'tr' ? 'Faydalı Yük' : 'Payload Capacity' },
    { key: 'altitude', icon: Mountain, label: language === 'tr' ? 'Operasyonel İrtifa' : 'Operating Altitude' },
    { key: 'propulsion', icon: Radio, label: language === 'tr' ? 'İtki Sistemi' : 'Propulsion System' }
  ];

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="vehicle-detail-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div 
          className="absolute inset-0 bg-cover bg-center cursor-pointer"
          style={{ backgroundImage: `url('${getImageUrl(vehicle.image)}')` }}
          onClick={() => {
            setLightboxImage(vehicle.image);
            setLightboxIndex(0);
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to top, rgba(15, 23, 42, 1) 0%, rgba(15, 23, 42, 0.7) 40%, rgba(15, 23, 42, 0.3) 100%)'
          }}></div>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/aircraft')}
          className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
          style={{ 
            background: 'rgba(0, 0, 0, 0.5)', 
            backdropFilter: 'blur(8px)',
            color: '#F8FAFC',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          {language === 'tr' ? 'Geri' : 'Back'}
        </button>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 z-10">
          <div className="max-w-[1400px] mx-auto">
            {/* Category Badge */}
            <span 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              style={{ 
                background: 'rgba(0, 240, 255, 0.2)', 
                color: '#00F0FF',
                border: '1px solid rgba(0, 240, 255, 0.3)'
              }}
            >
              {vehicle.category === 'iha-s1' ? 'İHA-S1' : 
               vehicle.category === 'iha-s2' ? 'İHA-S2' : 
               language === 'tr' ? 'Prototip' : 'Prototype'}
            </span>

            {/* Vehicle Name */}
            <h1 
              className="text-5xl md:text-7xl font-bold mb-4"
              style={{ 
                color: '#F8FAFC',
                textShadow: '0 0 40px rgba(0, 240, 255, 0.5)'
              }}
            >
              {getLocalizedField(vehicle, 'name', language)}
            </h1>

            {/* Mission */}
            {vehicle.mission && (
              <p 
                className="text-xl md:text-2xl font-medium mb-6"
                style={{ color: '#00F0FF', textShadow: '0 0 20px rgba(0, 240, 255, 0.4)' }}
              >
                {getLocalizedField(vehicle, 'mission', language)}
              </p>
            )}

            {/* Description */}
            <p className="text-lg max-w-3xl" style={{ color: '#F8FAFC', opacity: 0.9 }}>
              {getLocalizedField(vehicle, 'description', language)}
            </p>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 px-[7.6923%]" style={{ background: 'rgba(30, 41, 59, 0.4)' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ 
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
          }}>
            {language === 'tr' ? 'Teknik Özellikler' : 'Technical Specifications'}
          </h2>

          {/* Specs Table */}
          <div 
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(0, 240, 255, 0.2)',
              boxShadow: '0 0 40px rgba(0, 240, 255, 0.1)'
            }}
            data-testid="specs-table"
          >
            <table className="w-full">
              <thead>
                <tr style={{ background: 'rgba(0, 240, 255, 0.1)' }}>
                  <th className="text-left p-6 text-lg font-semibold" style={{ color: '#00F0FF' }}>
                    {language === 'tr' ? 'Özellik' : 'Specification'}
                  </th>
                  <th className="text-left p-6 text-lg font-semibold" style={{ color: '#00F0FF' }}>
                    {language === 'tr' ? 'Değer' : 'Value'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {specsList.map((spec, index) => {
                  const value = getLocalizedSpec(specs, spec.key, language);
                  if (!value) return null;
                  
                  return (
                    <tr 
                      key={spec.key}
                      style={{ 
                        borderBottom: index < specsList.length - 1 ? '1px solid rgba(0, 240, 255, 0.1)' : 'none'
                      }}
                    >
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ background: 'rgba(0, 240, 255, 0.1)' }}
                          >
                            <spec.icon size={24} style={{ color: '#00F0FF' }} />
                          </div>
                          <span className="font-medium" style={{ color: '#F8FAFC' }}>
                            {spec.label}
                          </span>
                        </div>
                      </td>
                      <td className="p-6">
                        <span 
                          className="text-xl font-bold"
                          style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.3)' }}
                        >
                          {value}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Additional Gallery & Video */}
      {(allImages.length > 1 || youtubeId) && (
        <section className="py-20 px-[7.6923%]" style={{ background: '#0F172A' }}>
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ 
              color: '#F8FAFC',
              textShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
            }}>
              {language === 'tr' ? 'Medya Galerisi' : 'Media Gallery'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Video Card */}
              {youtubeId && (
                <div 
                  className="relative rounded-xl overflow-hidden cursor-pointer group"
                  style={{ aspectRatio: '16/9' }}
                  onClick={() => setShowVideo(true)}
                  data-testid="video-card"
                >
                  <img
                    src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                    }}
                  />
                  <div 
                    className="absolute inset-0 flex items-center justify-center transition-all duration-300"
                    style={{ background: 'rgba(0, 0, 0, 0.4)' }}
                  >
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ 
                        background: 'rgba(255, 0, 0, 0.9)',
                        boxShadow: '0 0 30px rgba(255, 0, 0, 0.5)'
                      }}
                    >
                      <Play size={36} fill="white" style={{ color: 'white', marginLeft: '4px' }} />
                    </div>
                  </div>
                  <div 
                    className="absolute bottom-3 left-3 px-3 py-1 rounded-lg text-sm font-medium"
                    style={{ background: 'rgba(0, 0, 0, 0.8)', color: '#F8FAFC' }}
                  >
                    {language === 'tr' ? 'Video İzle' : 'Watch Video'}
                  </div>
                </div>
              )}

              {/* Additional Images */}
              {allImages.slice(1).map((img, index) => (
                <div 
                  key={index}
                  className="relative rounded-xl overflow-hidden cursor-pointer group"
                  style={{ 
                    aspectRatio: '16/9',
                    minHeight: '200px',
                    maxHeight: '300px'
                  }}
                  onClick={() => {
                    setLightboxImage(img);
                    setLightboxIndex(index + 1);
                  }}
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`${vehicle.name} - ${index + 2}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ objectPosition: 'center center' }}
                    onError={(e) => {
                      e.target.style.objectFit = 'contain';
                      e.target.style.background = 'rgba(30, 41, 59, 0.8)';
                    }}
                  />
                  <div 
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                    style={{ background: 'rgba(0, 0, 0, 0.5)' }}
                  >
                    <div 
                      className="px-4 py-2 rounded-lg"
                      style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}
                    >
                      {language === 'tr' ? 'Büyüt' : 'Enlarge'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Gallery CTA */}
      <section className="py-16 px-[7.6923%]" style={{ background: 'rgba(30, 41, 59, 0.4)' }}>
        <div className="max-w-[1400px] mx-auto text-center">
          <Link
            to="/aircraft"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl font-medium text-lg transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)',
              color: '#0F172A',
              boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 0 50px rgba(0, 240, 255, 0.5)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <ArrowLeft size={24} />
            {language === 'tr' ? 'Tüm Araçları Görüntüle' : 'View All Vehicles'}
          </Link>
        </div>
      </section>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
              style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
            >
              <X size={20} style={{ color: '#F8FAFC' }} />
            </button>

            {/* Navigation */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={() => navigateLightbox('prev')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-14 w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: 'rgba(0, 240, 255, 0.2)', border: '1px solid rgba(0, 240, 255, 0.3)' }}
                >
                  <ChevronLeft size={28} style={{ color: '#00F0FF' }} />
                </button>
                <button
                  onClick={() => navigateLightbox('next')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-14 w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: 'rgba(0, 240, 255, 0.2)', border: '1px solid rgba(0, 240, 255, 0.3)' }}
                >
                  <ChevronRight size={28} style={{ color: '#00F0FF' }} />
                </button>
              </>
            )}

            <img
              src={getImageUrl(allImages[lightboxIndex])}
              alt={vehicle.name}
              className="w-full h-auto max-h-[85vh] object-contain rounded-xl"
            />

            {/* Image Counter */}
            {allImages.length > 1 && (
              <div className="mt-4 text-center" style={{ color: '#94A3B8' }}>
                {lightboxIndex + 1} / {allImages.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideo && youtubeId && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
          onClick={() => setShowVideo(false)}
          data-testid="video-modal"
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
              style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
            >
              <X size={20} style={{ color: '#F8FAFC' }} />
            </button>

            <div className="rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                title={vehicle.name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
